import React from 'react';
import { useSnackbar } from 'notistack';
import { DashboardWidget, newWidgetId } from '../../utils/workspaceDashboardWidgets';
import { SettingsCard } from '../common';
import { AddIcon, CheckIcon, CloseIcon, DeleteIcon, EditIcon, FileTextIcon, PlayIcon } from '../icons';
import { Box, Button, IconButton, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '../ui';
import { alert } from '../../hooks/function';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

/**
 * Per-widget UI-only state. Kept separate from the data model so the
 * saved shape stays clean and round-trips through serialize/parse
 * without editor cruft. `originalSnapshot` is captured when edit mode
 * opens — null means the widget is brand-new, so Cancel removes it
 * instead of reverting.
 */
export type WidgetEditState = {
  editing: boolean;
  viewMode: 'render' | 'code';
  originalSnapshot: DashboardWidget | null;
};

interface WorkspaceDashboardTabProps {
  // Banner. The saved URL / selected file live in the page orchestrator
  // (its handleSave uploads + serializes them), so this tab is controlled:
  // it renders the preview + draft and calls back for the mutations.
  bannerImagePreview: string | null;
  bannerImageUrlDraft: string;
  setBannerImageUrlDraft: (value: string) => void;
  onBannerFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBannerUrlBlur: () => void;
  onBannerRemove: () => void;
  // Widgets. State stays in the orchestrator for the same reason (handleSave
  // serializes `dashboardWidgets` into the single backend field).
  dashboardWidgets: DashboardWidget[];
  setDashboardWidgets: React.Dispatch<React.SetStateAction<DashboardWidget[]>>;
  widgetEditStates: Record<string, WidgetEditState>;
  setWidgetEditStates: React.Dispatch<React.SetStateAction<Record<string, WidgetEditState>>>;
}

/**
 * WorkspaceDashboardTab
 *
 * The Dashboard tab body for the workspace editor: a banner (upload or
 * image URL) plus a list of raw-HTML widget blocks shown above the
 * course tabs on the learner dashboard. Extracted from WorkspaceEdit as
 * a CONTROLLED component — the banner/widget state stays in the page so
 * its shared save bar (General-tab `handleSave`) can serialize it; this
 * component only renders and edits.
 */
export default function WorkspaceDashboardTab({
  bannerImagePreview,
  bannerImageUrlDraft,
  setBannerImageUrlDraft,
  onBannerFileSelect,
  onBannerUrlBlur,
  onBannerRemove,
  dashboardWidgets,
  setDashboardWidgets,
  widgetEditStates,
  setWidgetEditStates,
}: WorkspaceDashboardTabProps) {
  const { enqueueSnackbar } = useSnackbar();

  const getWidgetEditState = (id: string): WidgetEditState =>
    widgetEditStates[id] ?? { editing: false, viewMode: 'render', originalSnapshot: null };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 920 }}>
      {/* ── Banner ───────────────────────────────────────────────
          Image-only. Two ways in: upload a file (SAS pipeline on
          save) or paste a full image URL (stored verbatim in
          bannerImageUrl). Whichever was set last wins. */}
      <SettingsCard
        title="Banner"
        subtitle="Image shown on the learner dashboard. Upload one, or point to an image URL."
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Button variant="outlined" component="label" size="small">
            {bannerImagePreview ? 'Replace image' : 'Upload image'}
            <input hidden accept="image/*" type="file" onChange={onBannerFileSelect} />
          </Button>
          {bannerImagePreview && (
            <Button variant="text" size="small" color="error" onClick={onBannerRemove}>
              Remove
            </Button>
          )}
        </Box>

        <TextField
          label="…or paste an image URL"
          fullWidth
          size="small"
          value={bannerImageUrlDraft}
          onChange={(e) => setBannerImageUrlDraft(e.target.value)}
          onBlur={onBannerUrlBlur}
          placeholder="https://…"
          helperText="Direct link to an image (PNG / JPG / SVG). Used when no file is uploaded."
        />

        {bannerImagePreview && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
              Preview
            </Typography>
            <Box
              component="img"
              src={bannerImagePreview}
              alt="Banner preview"
              sx={{ display: 'block', maxWidth: '100%', maxHeight: 220, objectFit: 'contain', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
            />
          </Box>
        )}
      </SettingsCard>

      {/* ── Dashboard widgets ────────────────────────────────────
          Each widget is a raw-HTML block with an optional title.
          Persisted into the single backend `dashboardWidgetHtml`
          field with invisible <!--WIDGET--> markers so multiple
          blocks round-trip server-side. Learner dashboard renders
          the concatenated HTML verbatim. */}
      <SettingsCard
        title="Dashboard Widgets"
        subtitle="Custom HTML blocks shown above the course tabs on the learner dashboard. Add as many as you need."
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {dashboardWidgets.length === 0 ? (
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'divider',
                bgcolor: 'grey.50',
                color: 'text.secondary',
                textAlign: 'center',
              }}
            >
              <Typography variant="body2">
                No widgets yet. Click <strong>Add widget</strong> to drop in custom HTML — announcements, welcome messages, embedded videos, anything.
              </Typography>
            </Box>
          ) : (
            dashboardWidgets.map((w, idx) => {
              const ui = getWidgetEditState(w.id);
              const isEditing = ui.editing;
              return (
                <Box
                  key={w.id}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: isEditing ? 'primary.main' : 'divider',
                    bgcolor: 'background.paper',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    transition: 'border-color 150ms ease',
                  }}
                >
                  {/* Header strip — eyebrow + (when saved) the
                      Render/Code toggle on the right, and the
                      Edit/Remove icons. When editing, the toggle
                      is hidden (the textarea IS the code view). */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      Widget {idx + 1}
                      {w.title ? (
                        <Box component="span" sx={{ ml: 1, color: 'text.secondary', textTransform: 'none', fontWeight: 600, letterSpacing: 0 }}>
                          · {w.title}
                        </Box>
                      ) : null}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    {!isEditing && (
                      <ToggleButtonGroup
                        size="small"
                        value={ui.viewMode}
                        exclusive
                        onChange={(_e, next: 'render' | 'code' | null) => {
                          if (!next) return;
                          setWidgetEditStates((prev) => ({
                            ...prev,
                            [w.id]: { ...getWidgetEditState(w.id), viewMode: next },
                          }));
                        }}
                      >
                        <ToggleButton value="render" sx={{ textTransform: 'none', px: 1.25, py: 0.25, fontSize: '0.75rem' }}>
                          <PlayIcon sx={{ fontSize: 14, mr: 0.5 }} />
                          Render
                        </ToggleButton>
                        <ToggleButton value="code" sx={{ textTransform: 'none', px: 1.25, py: 0.25, fontSize: '0.75rem' }}>
                          <FileTextIcon sx={{ fontSize: 14, mr: 0.5 }} />
                          Code
                        </ToggleButton>
                      </ToggleButtonGroup>
                    )}
                    {!isEditing && (
                      <Tooltip title="Edit widget">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setWidgetEditStates((prev) => ({
                              ...prev,
                              [w.id]: { editing: true, viewMode: 'code', originalSnapshot: { ...w } },
                            }))
                          }
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Remove widget">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDashboardWidgets((cur) => cur.filter((x) => x.id !== w.id));
                          setWidgetEditStates((prev) => {
                            const next = { ...prev };
                            delete next[w.id];
                            return next;
                          });
                        }}
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Body — editor inputs OR saved view (render / code). */}
                  {isEditing ? (
                    <>
                      <TextField
                        label="Title (optional)"
                        size="small"
                        fullWidth
                        value={w.title}
                        onChange={(e) => {
                          const title = e.target.value;
                          setDashboardWidgets((cur) =>
                            cur.map((x) => (x.id === w.id ? { ...x, title } : x)),
                          );
                        }}
                        placeholder="Short label so you can tell widgets apart"
                        helperText="Admin-only — not shown to learners."
                      />

                      <TextField
                        label="HTML"
                        multiline
                        minRows={6}
                        fullWidth
                        value={w.html}
                        onChange={(e) => {
                          const html = e.target.value;
                          setDashboardWidgets((cur) =>
                            cur.map((x) => (x.id === w.id ? { ...x, html } : x)),
                          );
                        }}
                        placeholder={'<div>Welcome back, team!</div>'}
                        InputProps={{
                          sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                        }}
                        helperText="Raw HTML — rendered verbatim on the learner dashboard."
                      />

                      {/* Per-widget Save / Cancel. Save validates
                          that there's at least some HTML (an empty
                          widget would be invisible to learners and
                          get dropped on workspace-save anyway).
                          Cancel removes brand-new widgets entirely;
                          for existing widgets, it reverts to the
                          snapshot taken when edit mode opened. */}
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          color="inherit"
                          startIcon={<CloseIcon />}
                          onClick={() => {
                            const snap = ui.originalSnapshot;
                            if (!snap) {
                              // Brand-new widget — drop it.
                              setDashboardWidgets((cur) => cur.filter((x) => x.id !== w.id));
                              setWidgetEditStates((prev) => {
                                const next = { ...prev };
                                delete next[w.id];
                                return next;
                              });
                            } else {
                              // Existing widget — revert to the snapshot.
                              setDashboardWidgets((cur) =>
                                cur.map((x) => (x.id === w.id ? snap : x)),
                              );
                              setWidgetEditStates((prev) => ({
                                ...prev,
                                [w.id]: { editing: false, viewMode: 'render', originalSnapshot: null },
                              }));
                            }
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CheckIcon />}
                          onClick={() => {
                            if (!w.html.trim()) {
                              alert(enqueueSnackbar, 'Add some HTML before saving the widget.', 'warning');
                              return;
                            }
                            setWidgetEditStates((prev) => ({
                              ...prev,
                              [w.id]: { editing: false, viewMode: 'render', originalSnapshot: null },
                            }));
                          }}
                          sx={{
                            '&.Mui-disabled': { color: '#fff', opacity: 0.65 },
                          }}
                        >
                          Save widget
                        </Button>
                      </Box>
                    </>
                  ) : ui.viewMode === 'render' ? (
                    // Rendered preview — same dangerouslySetInnerHTML
                    // path the learner dashboard uses (TiptapViewer
                    // ultimately renders the concatenated HTML
                    // verbatim, but here we render the per-widget
                    // slice). Wrapped in a softly-bordered surface
                    // so the preview reads as a "this is what
                    // learners see" panel.
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'grey.50',
                        '& *:first-of-type': { mt: 0 },
                        '& *:last-of-type': { mb: 0 },
                      }}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(w.html) }}
                    />
                  ) : (
                    // Code view — raw HTML in a monospace block so
                    // admins can verify exactly what's stored.
                    <Box
                      component="pre"
                      sx={{
                        p: 2,
                        m: 0,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: '#0f172a',
                        color: '#e2e8f0',
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: 320,
                        overflow: 'auto',
                      }}
                    >
                      {w.html}
                    </Box>
                  )}
                </Box>
              );
            })
          )}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              const id = newWidgetId();
              setDashboardWidgets((cur) => [...cur, { id, title: '', html: '' }]);
              // New widget opens in edit mode by default — the admin
              // wants to type immediately, not click Edit first.
              setWidgetEditStates((prev) => ({
                ...prev,
                [id]: { editing: true, viewMode: 'code', originalSnapshot: null },
              }));
            }}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add widget
          </Button>
        </Box>
      </SettingsCard>
    </Box>
  );
}
